import React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import Tooltip from '../ScrollDismissTooltip';
import { getAddressKey } from '../utils/addresses';

const pendingRecordTooltip = 'Choose Number of Homes or Radius Distance.';

const renderAddressRecordLabel = (label, status) => {
  if (!label) return null;

  if (status !== 'pending' || label !== 'Pending') {
    return <span>{label}</span>;
  }

  return (
    <Tooltip
      arrow
      describeChild
      disableInteractive
      placement="top"
      title={pendingRecordTooltip}
    >
      <span>{label}</span>
    </Tooltip>
  );
};

const SummaryPanel = ({
  activeAddressIndex,
  canClearSelection,
  canPurchaseList,
  contactTypeCopy,
  costPerRecordLabel,
  filterDraftCount,
  focusAddressInput,
  focusManualAreaOnMap,
  getAddressSummaryRecordMeta,
  getDisplayAddress,
  handleBackToCreateNewOrder,
  handleSelectStartingAddress,
  inputEntityLabel,
  isFindHomesAroundLocationsMode,
  isFilterDraftInvalid,
  isManualAreaMode,
  isSummaryHomesLoading,
  listAddressConfigs,
  listCleanupOptionsPanel,
  hasPendingFilterDraftChanges,
  onApplyFilters,
  onCancelFilters,
  openClearListConfirm,
  openPurchaseConfirm,
  pendingFilterChangeCount,
  retryManualAreaEstimate,
  retryStartingAddressEstimate,
  removeManualArea,
  removeStartingAddress,
  selectedManualAreaKey,
  summaryEmptyAddressActionLabel,
  startingAddressCount,
  startingAddressSummaryTitle,
  summaryAddressListRef,
  summaryDuplicatesText,
  summaryHomesCount,
  summaryLocationItems,
  summaryPrimaryLabel,
  summaryPrimaryValue,
  summaryRows,
  summaryStatValueStyle,
  summaryTotalCostValue,
}) => {
  const renderAddressTitleTooltip = React.useCallback((title) => (
    <Tooltip
      arrow
      describeChild
      disableInteractive
      placement="top"
      title={title}
    >
      <span className="summaryAddressText">{title}</span>
    </Tooltip>
  ), []);
  const summaryListItemCount = isFindHomesAroundLocationsMode && isManualAreaMode
    ? summaryLocationItems.length
    : startingAddressCount;
  const hasSummaryListScrollbarGutter = summaryListItemCount > 3;
  const pendingFilterTitle = pendingFilterChangeCount > 0
    ? `${pendingFilterChangeCount} filter change${pendingFilterChangeCount === 1 ? '' : 's'} not applied`
    : 'Filter changes not applied';
  const applyFiltersLabel = filterDraftCount > 0
    ? `Apply ${filterDraftCount} filter${filterDraftCount === 1 ? '' : 's'}`
    : 'Apply filters';

  return (
    <Box className="summaryPanel">
    <Box className="summaryCard startingAddressesCard">
      <Box className="summaryCardHeader">
        <Typography className="summaryTitle">{startingAddressSummaryTitle}</Typography>
        <button
          className="summaryClearButton"
          disabled={!canClearSelection}
          onClick={openClearListConfirm}
          type="button"
        >
          Clear Selection
        </button>
      </Box>
      <Box className="summaryAddressListShell">
        <Box
          className={hasSummaryListScrollbarGutter ? 'summaryAddressList hasScrollbarGutter' : 'summaryAddressList'}
          ref={summaryAddressListRef}
        >
          {((isFindHomesAroundLocationsMode && isManualAreaMode)
            ? summaryLocationItems.length === 0
            : startingAddressCount === 0) && (
            <Box className="summaryEmptyAddress">
              <Button
                className="summaryEmptyAddressButton"
                onClick={focusAddressInput}
                startIcon={<AddIcon />}
              >
                {summaryEmptyAddressActionLabel}
              </Button>
            </Box>
          )}
          {isFindHomesAroundLocationsMode && isManualAreaMode ? summaryLocationItems.map((item) => (
            <Box
              className={item.area.key === selectedManualAreaKey ? 'summaryAddressItem selected' : 'summaryAddressItem'}
              key={item.area.key}
            >
              <button
                aria-label={`${item.number} ${item.title}`}
                className="summaryAddressSelect"
                onClick={() => focusManualAreaOnMap(item.area)}
                type="button"
              >
                <span className="summaryAddressNumber">{item.number}</span>
                <span className="summaryAddressTooltipAnchor">
                  {renderAddressTitleTooltip(item.title)}
                </span>
                <span className={`summaryAddressRecords ${item.recordsStatus === 'loading' ? 'loading' : ''} ${item.recordsStatus === 'error' ? 'error' : ''}`}>
                  {item.recordsStatus === 'loading' && <span className="summaryAddressSpinner" aria-hidden="true" />}
                  {renderAddressRecordLabel(item.recordsLabel, item.recordsStatus)}
                </span>
              </button>
              <Box className="summaryAddressActions">
                {item.recordsStatus === 'error' && (
                  <Tooltip arrow disableInteractive placement="top" title="Retry count">
                    <IconButton
                      aria-label={`Retry count for location ${item.number}`}
                      className="summaryAddressRetry"
                      onClick={() => retryManualAreaEstimate(item.area)}
                    >
                      <ReplayOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  aria-label={`Remove location ${item.number}`}
                  className="summaryAddressRemove"
                  onClick={() => removeManualArea(item.area)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            </Box>
          )) : listAddressConfigs.map((config, index) => {
            const isActiveAddress = index === activeAddressIndex;
            const addressRecordMeta = getAddressSummaryRecordMeta(config, index);
            const displayAddress = getDisplayAddress(config.address);

            return (
              <Box
                className={isActiveAddress ? 'summaryAddressItem selected' : 'summaryAddressItem'}
                key={getAddressKey(config.address)}
              >
                <button
                  aria-label={`${index + 1} ${displayAddress}`}
                  className="summaryAddressSelect"
                  onClick={() => handleSelectStartingAddress(index)}
                  type="button"
                >
                  <span className="summaryAddressNumber">{index + 1}</span>
                  <span className="summaryAddressTooltipAnchor">
                    {renderAddressTitleTooltip(displayAddress)}
                  </span>
                  <span className={`summaryAddressRecords ${addressRecordMeta.status === 'loading' ? 'loading' : ''} ${addressRecordMeta.status === 'error' ? 'error' : ''}`}>
                    {addressRecordMeta.status === 'loading' && <span className="summaryAddressSpinner" aria-hidden="true" />}
                    {renderAddressRecordLabel(addressRecordMeta.label, addressRecordMeta.status)}
                  </span>
                </button>
                <Box className="summaryAddressActions">
                  {addressRecordMeta.status === 'error' && (
                    <Tooltip arrow disableInteractive placement="top" title="Retry count">
                      <IconButton
                        aria-label={`Retry count for ${displayAddress}`}
                        className="summaryAddressRetry"
                        onClick={() => retryStartingAddressEstimate(config, index)}
                      >
                        <ReplayOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    aria-label={`Remove ${inputEntityLabel}`}
                    className="summaryAddressRemove"
                    onClick={() => removeStartingAddress(index)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>

    <Box className="summaryCard">
      <Typography className="summaryTitle">Current List Summary</Typography>
      <Box className="summaryStats" style={summaryStatValueStyle}>
        <Box className="summaryStat primary">
          <Box className="summaryStatLabel">
            <Typography>{summaryPrimaryLabel}</Typography>
            {summaryPrimaryLabel === contactTypeCopy.uniqueTitle && (
              <Tooltip
                arrow
                disableInteractive
                placement="top"
                title={`Each ${contactTypeCopy.singular} is counted once, even if it falls inside more than one area/radius you've added.`}
              >
                <IconButton
                  aria-label={`Unique ${contactTypeCopy.plural} information`}
                  className="summaryStatInfoButton"
                  size="small"
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <strong
            className={[
              summaryHomesCount === null && !isSummaryHomesLoading ? 'summaryPlaceholder' : '',
              isSummaryHomesLoading ? 'summaryStatLoading' : '',
            ].filter(Boolean).join(' ')}
          >
            {isSummaryHomesLoading ? (
              <span className="summaryStatSpinner" aria-label={`Loading ${contactTypeCopy.plural} count`} role="status" />
            ) : summaryPrimaryValue}
          </strong>
        </Box>
        <Box className="summaryStat summaryCostStat">
          <Typography>Total Cost</Typography>
          <strong
            className={[
              summaryHomesCount === null && !isSummaryHomesLoading ? 'summaryPlaceholder' : '',
              isSummaryHomesLoading ? 'summaryStatLoading' : '',
            ].filter(Boolean).join(' ')}
          >
            {isSummaryHomesLoading ? (
              <span className="summaryStatSpinner" aria-label="Loading total cost" role="status" />
            ) : summaryTotalCostValue}
          </strong>
          <span className="summaryStatSubtext">{costPerRecordLabel}</span>
        </Box>
      </Box>
      {summaryDuplicatesText && (
        <Box className="summaryDuplicatesNote listCleanupOption listCleanupNote">
          <span className="listCleanupText">{summaryDuplicatesText}</span>
        </Box>
      )}
      <Box className="summaryInfoRows">
        {summaryRows.map((row) => (
          <Box className="summaryInfoRow" key={row.label}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </Box>
        ))}
        {hasPendingFilterDraftChanges && (
          <Box className="summaryPendingFilters" role="alert">
            <Box className="summaryPendingFiltersMessage">
              <WarningAmberRoundedIcon aria-hidden="true" />
              <Box>
                <strong>{pendingFilterTitle}</strong>
                <span>Apply your filters to update the record count and price.</span>
              </Box>
            </Box>
            <Box className="summaryPendingFilterActions">
              <Button
                className="summaryCancelFiltersButton"
                onClick={onCancelFilters}
              >
                Cancel filters
              </Button>
              <Button
                className="summaryApplyFiltersButton"
                disabled={isFilterDraftInvalid}
                onClick={onApplyFilters}
              >
                {applyFiltersLabel}
              </Button>
            </Box>
          </Box>
        )}
        {listCleanupOptionsPanel && (
          <Box className="summaryInfoRow summaryCleanupRow">
            <Box className="summaryCleanupOptions">
              {listCleanupOptionsPanel}
            </Box>
          </Box>
        )}
      </Box>
      {hasPendingFilterDraftChanges && (
        <Typography className="summaryPurchaseBlockedMessage">
          Apply filters before purchasing.
        </Typography>
      )}
      <Button
        className="summaryPurchasePrimaryButton"
        disabled={!canPurchaseList}
        onClick={() => openPurchaseConfirm('continue_to_mail')}
      >
        Purchase List and Send Mail
      </Button>
      <Button
        className="summaryPurchaseSecondaryButton"
        disabled={!canPurchaseList}
        onClick={() => openPurchaseConfirm('exit')}
      >
        Purchase List and Exit
      </Button>
      <Button
        className="summaryBackTextButton"
        startIcon={
          <svg
            fill="none"
            height="10"
            viewBox="0 0 14 10"
            width="14"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 9L1 5M1 5L5 1M1 5L13 5"
              stroke="black"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity="0.6"
              strokeWidth="1.5"
            />
          </svg>
        }
        onClick={handleBackToCreateNewOrder}
      >
        Back To Create New Order
      </Button>
    </Box>
  </Box>
  );
};

export default SummaryPanel;
